/**
 * The timeline's data model, and the only place that knows its shape.
 *
 * The graph stays in the `layered_timeline_v1` format the backend already
 * produces and consumes, so AI proposals, the approval flow and saved
 * workspaces are unaffected. What this module adds is the vocabulary an NLE
 * needs and the old format lacked:
 *
 *   start / end / duration   where the clip sits ON THE TRACK
 *   sourceIn / sourceOut     which part of the SOURCE FILE it plays
 *
 * Keeping those apart is what makes a left-trim survive. The old model had
 * only a duration, so "I moved the in-point 2s later" and "this clip is 2s
 * shorter" were the same edit — and the renderer guessed wrong, always
 * emitting trim(clip, 0, duration).
 */

export const TIMELINE_FORMAT = 'layered_timeline_v1';

/** Shortest clip a trim or split may produce. Below this a clip is unclickable. */
export const MIN_CLIP_DURATION = 0.1;

/** A clip plays its source at its recorded loudness unless told otherwise. */
export const DEFAULT_CLIP_VOLUME = 1;

/** Loudest a clip may be pushed. Past this the source clips rather than gets louder. */
export const MAX_CLIP_VOLUME = 2;

export const deepClone = (value) => {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return value;
    }
};

export const toSafeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const isVideoAssetType = (fileType) => String(fileType || '').toLowerCase().startsWith('video/');
export const isAudioAssetType = (fileType) => String(fileType || '').toLowerCase().startsWith('audio/');
export const isImageAssetType = (fileType) => String(fileType || '').toLowerCase().startsWith('image/');

export const getAssetLayerKind = (fileType) => {
    if (isAudioAssetType(fileType)) return 'audio';
    if (isVideoAssetType(fileType)) return 'video';
    return '';
};

export const sanitizeStreamName = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const formatSeconds = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return '0:00';
    const minutes = Math.floor(numeric / 60);
    const seconds = numeric - minutes * 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(1)}`;
};

/** Compact seconds for clip chips and ruler ticks: "4s", "2.5s". */
export const formatTimeLabel = (value) => {
    const numeric = toSafeNumber(value);
    if (numeric === null) return '';
    if (Math.abs(numeric - Math.round(numeric)) < 1e-6) return `${Math.round(numeric)}s`;
    return `${numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}s`;
};

/**
 * How loud this clip plays, as a multiplier of its source.
 *
 * One number carries both controls the inspector shows, because that is how
 * the render side models it too: the backend's volume tool documents muting as
 * volume=0 rather than as a separate operation, so "quieter" and "silent" stay
 * the same control end to end and cannot disagree with each other.
 */
export const clipVolume = (item) => {
    const level = toSafeNumber(item?.volume);
    if (level === null) return DEFAULT_CLIP_VOLUME;
    return Math.max(0, Math.min(MAX_CLIP_VOLUME, level));
};

export const isClipMuted = (item) => clipVolume(item) === 0;

/** Does this clip's sound need saying in the script at all? */
export const hasCustomVolume = (item) => clipVolume(item) !== DEFAULT_CLIP_VOLUME;

/* ---- multicam: one moment, several cameras --------------------------------
 *
 * A clip may carry several `angles` of the same take instead of one source.
 * The cameras were not started together, so each angle records the `offset`
 * between its own first frame and the shared master clock: master time minus
 * the offset is where that moment sits inside that camera's file.
 *
 * The clip's start/sourceIn/sourceOut stay in MASTER time, which is what makes
 * switching angles a property edit rather than a re-cut -- the segment keeps
 * the span it always had and only the camera reading it changes. Cutting
 * between angles is therefore the split that already exists, and the emitted
 * script is still trim + concat.
 *
 * `asset_filename` is kept pointing at the live angle, so everything that
 * resolves a clip to a file -- the preview, the media check, the label --
 * follows the switch without knowing multicam exists.
 */

export const clipAngles = (item) => (Array.isArray(item?.angles) ? item.angles : []);

export const isMulticam = (item) => clipAngles(item).length > 1;

const clampIndex = (value, length) => {
    const index = Math.round(toSafeNumber(value) ?? 0);
    return Math.max(0, Math.min(Math.max(0, length - 1), index));
};

/** Which angle is on screen for this segment. */
export const angleIndexOf = (item) => clampIndex(item?.angleIndex, clipAngles(item).length);

/** Which angle the sound comes from — one choice for the whole group. */
export const audioAngleIndexOf = (item) => clampIndex(item?.audioAngleIndex, clipAngles(item).length);

export const activeAngle = (item) => clipAngles(item)[angleIndexOf(item)] || null;

export const audioAngle = (item) => clipAngles(item)[audioAngleIndexOf(item)] || null;

export const angleOffset = (angle) => toSafeNumber(angle?.offset) ?? 0;

/** Where a master-clock moment falls inside one angle's own file. */
export const angleSourceTime = (angle, masterSeconds) => masterSeconds - angleOffset(angle);

export const angleLabel = (angle, index) => (
    String(angle?.label || '').trim() || `Angle ${index + 1}`
);

/** An angle a clip may be normalised from: a bare filename or a full record. */
const normalizeAngle = (angle) => {
    if (typeof angle === 'string') {
        return angle.trim() ? { asset_filename: angle.trim(), offset: 0, label: '' } : null;
    }
    const fileName = String(angle?.asset_filename || angle?.filename || '').trim();
    if (!fileName) return null;
    return {
        asset_filename: fileName,
        offset: toSafeNumber(angle?.offset) ?? 0,
        label: String(angle?.label || '').trim(),
    };
};

export const normalizeAngles = (angles) => (
    (Array.isArray(angles) ? angles : []).map(normalizeAngle).filter(Boolean)
);

export const getLayerKind = (layer) => {
    const kind = String(layer?.kind || '').toLowerCase();
    const name = String(layer?.name || '').toLowerCase();
    if (kind === 'video' || name === 'layer 1') return 'video';
    if (kind === 'audio' || name === 'layer 2') return 'audio';
    if (kind === 'effects' || name === 'layer 0') return 'effects';
    if (kind === 'overlay') return 'overlay';
    return '';
};

/* ---- the overlay track: video over video ---------------------------------
 *
 * Clips on it are picture-in-picture insets. Unlike the video track it is not
 * kept gapless -- an inset sits at the time it was placed and nowhere else --
 * and it is laid over the finished main sequence rather than cut into it, so
 * one inset can run across any number of cuts underneath.
 *
 * Where an inset sits on screen is `params` {x, y, scale}: its normalized
 * centre and its width as a fraction of the frame's, the same vocabulary a
 * sticker uses. Keeping it under `params` is deliberate -- it is what the stage
 * already edits when anything is dragged on it.
 */

/** Asset kinds the overlay track accepts. */
export const OVERLAY_ACCEPTS = 'video';

export const DEFAULT_PIP = { x: 0.75, y: 0.25, scale: 0.35 };

export const pipParams = (clip) => ({ ...DEFAULT_PIP, ...(clip?.params || {}) });

/**
 * Whose sound is heard while an inset is on screen.
 *
 *   main   the video underneath, as if the inset were silent (the default)
 *   inset  the inset alone; the main's sound drops out for its span
 *   both   the two mixed
 *
 * Insets placed before the choice existed carry only a volume: muted meant
 * "main", anything audible meant it was mixed in, i.e. "both".
 */
export const INSET_SOUNDS = ['main', 'inset', 'both'];

export const insetSoundFrom = (clip) => {
    if (INSET_SOUNDS.includes(clip?.soundFrom)) return clip.soundFrom;
    return isClipMuted(clip) ? 'main' : 'both';
};

/**
 * (width, height, left, top) in pixels for something laid over a frame.
 *
 * The same arithmetic as effect_blocks.overlay_placement on the backend, line
 * for line: the preview draws the inset where this says, and the render puts
 * it where that says, so the two must not be allowed to differ.
 */
export const overlayPlacement = (videoW, videoH, overlayW, overlayH, x, y, scale) => {
    const clamp01 = value => Math.max(0, Math.min(1, Number(value) || 0));
    const fraction = Math.max(0.02, Math.min(1, Number(scale) || 0));

    let width = Math.max(2, Math.round(fraction * videoW));
    width -= width % 2;
    let height = Math.max(2, Math.round(width * overlayH / overlayW));
    height -= height % 2;
    if (height > videoH) {
        height = videoH - (videoH % 2);
        width = Math.max(2, Math.round(height * overlayW / overlayH));
        width -= width % 2;
    }

    const left = Math.max(0, Math.min(videoW - width, Math.round(clamp01(x) * videoW) - Math.floor(width / 2)));
    const top = Math.max(0, Math.min(videoH - height, Math.round(clamp01(y) * videoH) - Math.floor(height / 2)));
    return { width, height, left, top };
};

/** The end of the main sequence: nothing plays past it, insets included. */
export const mainSequenceEnd = (graph) => (
    sortedClips(getLayerByKind(graph, 'video', 'layer 1'))
        .reduce((end, clip) => Math.max(end, toSafeNumber(clip?.end) ?? 0), 0)
);

export const getLayerByKind = (graph, kind, fallbackName) => {
    const layers = Array.isArray(graph?.layers) ? graph.layers : [];
    return layers.find(layer => String(layer?.kind || '').toLowerCase() === kind)
        || layers.find(layer => String(layer?.name || '').toLowerCase() === fallbackName)
        || null;
};

export const getLayerIndexByKind = (graph, kind, fallbackName) => {
    const layers = Array.isArray(graph?.layers) ? graph.layers : [];
    const byKind = layers.findIndex(layer => String(layer?.kind || '').toLowerCase() === kind);
    if (byKind >= 0) return byKind;
    return layers.findIndex(layer => String(layer?.name || '').toLowerCase() === fallbackName);
};

// The overlay layer goes LAST in the array even though it is drawn above the
// video track: saved graphs and AI proposals address the first three by index,
// and slotting a layer in between would move every one of them.
const createOverlayLayer = () => ({ name: 'Overlay', kind: 'overlay', items: [] });

export const createManualTimelineGraph = () => ({
    format: TIMELINE_FORMAT,
    total_duration: 0,
    layers: [
        { name: 'Effects', kind: 'effects', items: [] },
        { name: 'Video', kind: 'video', items: [] },
        { name: 'Audio', kind: 'audio', items: [] },
        createOverlayLayer(),
    ],
});

/** Top to bottom, the order the timeline draws its tracks in. */
const TRACK_DISPLAY_ORDER = ['effects', 'overlay', 'video', 'audio'];

export const trackDisplayRank = (layer) => {
    const rank = TRACK_DISPLAY_ORDER.indexOf(getLayerKind(layer));
    return rank < 0 ? TRACK_DISPLAY_ORDER.length : rank;
};

let idCounter = 0;
/** Stable per-item id. Selection and effect binding need something that
 *  survives a reorder, which an array index does not. */
export const createItemId = (prefix = 'item') => {
    idCounter += 1;
    return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
};

/**
 * Fill in what a clip needs but may not carry.
 *
 * Applies to clips from three sources, all of which predate the in/out model:
 * saved workspaces, AI proposals built by `_build_layered_plan_graph`, and
 * clips this editor wrote before the migration existed. All three are
 * untrimmed at the source, so sourceIn=0 / sourceOut=duration reproduces
 * exactly what they render today.
 */
export const migrateClip = (item, fallbackKind = 'video') => {
    if (!item || typeof item !== 'object') return null;

    const start = toSafeNumber(item.start) ?? 0;
    const end = toSafeNumber(item.end);
    const declaredDuration = toSafeNumber(item.duration);
    const duration = declaredDuration
        ?? (end !== null ? Math.max(MIN_CLIP_DURATION, end - start) : 1);

    const sourceIn = toSafeNumber(item.sourceIn) ?? 0;
    const sourceOut = toSafeNumber(item.sourceOut) ?? (sourceIn + duration);

    // Only a clip that actually has angles carries the multicam keys. Adding
    // empty ones to every clip would change the shape of every saved graph and
    // every AI proposal for a feature almost none of them use.
    const angles = normalizeAngles(item.angles);
    const multicam = angles.length > 0 ? {
        angles,
        angleIndex: clampIndex(item.angleIndex, angles.length),
        audioAngleIndex: clampIndex(item.audioAngleIndex, angles.length),
        multicamId: item.multicamId || createItemId('mc'),
    } : null;

    return {
        ...item,
        id: item.id || createItemId(fallbackKind),
        start,
        sourceIn,
        sourceOut,
        // Clips from every one of those three sources predate per-clip sound,
        // so they land at 1: unchanged from what they render today.
        volume: clipVolume(item),
        ...multicam,
        duration: Math.max(MIN_CLIP_DURATION, sourceOut - sourceIn),
        end: start + Math.max(MIN_CLIP_DURATION, sourceOut - sourceIn),
    };
};

/**
 * An effect item, in either of the two shapes the Effects layer holds.
 *
 * Ours is `{type, params}`. The planner's is keyed by `tool_name`
 * (video_edit_ai._build_layered_plan_graph). Mapping the planner's onto ours
 * is what lets one inspector edit both; a tool with no equivalent keeps its
 * tool_name and is shown read-only rather than silently dropped.
 */
const TOOL_NAME_TO_BLOCK_TYPE = {
    drawtext_tool: 'text',
    drawbox_tool: 'box',
    crop_tool: 'crop',
    zoompan_tool: 'zoom',
    overlay_tool: 'overlay',
    fade_tool: 'fade',
    vflip_tool: 'flip',
    hflip_tool: 'flip',
    hue_tool: 'look',
};

export const migrateEffect = (item) => {
    if (!item || typeof item !== 'object') return null;

    const toolName = String(item.tool_name || '').toLowerCase();
    const type = item.type || TOOL_NAME_TO_BLOCK_TYPE[toolName] || '';

    const start = toSafeNumber(item.start) ?? 0;
    const end = toSafeNumber(item.end);
    const duration = toSafeNumber(item.duration);

    return {
        ...item,
        id: item.id || createItemId('fx'),
        type,
        // No mapping means no editor panel can open it. Keeping the flag on the
        // item is how the inspector knows to show it read-only instead of
        // rendering an empty form over parameters it cannot interpret.
        readOnly: !type,
        params: item.params && typeof item.params === 'object' ? item.params : {},
        // A planner effect has no clipId: it is a step over the whole chain,
        // and the graph builder never recorded which clip it hit. Null means
        // "the whole timeline", which is exactly how it already renders.
        clipId: item.clipId || null,
        start,
        end: end ?? (duration !== null ? start + duration : start),
        duration,
    };
};

/** Bring a whole graph up to the current model. Idempotent. */
export const migrateGraph = (graph) => {
    const base = graph && typeof graph === 'object' && Array.isArray(graph.layers)
        ? deepClone(graph)
        : createManualTimelineGraph();

    base.format = base.format || TIMELINE_FORMAT;
    base.layers = (Array.isArray(base.layers) ? base.layers : []).map((layer) => {
        const kind = getLayerKind(layer);
        const items = Array.isArray(layer?.items) ? layer.items : [];
        return {
            ...layer,
            kind: layer?.kind || kind,
            items: items
                .map(item => (kind === 'effects' ? migrateEffect(item) : migrateClip(item, kind || 'video')))
                .filter(Boolean),
        };
    });

    // Graphs saved before insets existed have no lane to drop one on. An inset
    // needs a main video to sit over, so a graph without one is left alone.
    const hasVideo = base.layers.some(layer => getLayerKind(layer) === 'video');
    const hasOverlay = base.layers.some(layer => getLayerKind(layer) === 'overlay');
    if (hasVideo && !hasOverlay) base.layers.push(createOverlayLayer());

    return base;
};

/** The end of the last thing on any track. */
export const computeTotalDuration = (graph) => {
    const layers = Array.isArray(graph?.layers) ? graph.layers : [];
    return layers.reduce((maxEnd, layer) => (
        (Array.isArray(layer?.items) ? layer.items : []).reduce((layerMax, item) => {
            const start = toSafeNumber(item?.start) ?? 0;
            const end = toSafeNumber(item?.end) ?? (start + (toSafeNumber(item?.duration) ?? 0));
            return Math.max(layerMax, end);
        }, maxEnd)
    ), 0);
};

/** Clips in track order. The stored order is insertion order, not time order. */
export const sortedClips = (layer) => (
    (Array.isArray(layer?.items) ? [...layer.items] : [])
        .sort((a, b) => ((toSafeNumber(a?.start) ?? 0) - (toSafeNumber(b?.start) ?? 0)))
);

/** Re-derive end from start and the in/out points, after any edit. */
export const resealClip = (clip) => {
    const sourceIn = Math.max(0, toSafeNumber(clip.sourceIn) ?? 0);
    const sourceOut = Math.max(sourceIn + MIN_CLIP_DURATION, toSafeNumber(clip.sourceOut) ?? sourceIn + 1);
    const start = Math.max(0, toSafeNumber(clip.start) ?? 0);
    const duration = sourceOut - sourceIn;
    return { ...clip, sourceIn, sourceOut, start, duration, end: start + duration };
};

/**
 * Lay clips end to end from zero, preserving their order.
 *
 * This is the reflow that keeps a track gapless after an insert or a delete.
 * It moves clips in time but never touches their in/out points, so nothing
 * gets re-trimmed as a side effect of a neighbour moving.
 */
export const packSequentially = (clips) => {
    let cursor = 0;
    return clips.map((clip) => {
        const sealed = resealClip({ ...clip, start: cursor });
        cursor = sealed.end;
        return sealed;
    });
};
